import { UserFragment } from "src/state/gql/_types"
import { FormSchema } from "src/utils/forms"

export type UserCreateFragment = Omit<UserFragment, 'id' | 'created_at' | 'updated_at' | 'created_by_user' | 'updated_by_user' | 'status' | 'user_number' | 'user_role' | 'user_type' | 'user_status'>

export const UserCreateSchema: FormSchema<UserCreateFragment> = {
    id: "user",
    title: "User",
    groups: {
        details: {
            label: 'Details' 
        }
    },
    fields: {
        email: {
            group: 'details',
            label: "Email",
            type: "email",
            grid: 12,
            validation: {
                type: {
                    value: "string",
                    message: "{field} must be a string"
                },
                required: {
                    value: true,
                    message: "{field} is required"
                },
                email: {
                    message: "{field} is invalid"
                },
                email_available: {
                    message:  "{field} is invalid"
                }
            }
        },
        type: {
            group: 'details',
            label: "Department",
            type: "superadmin-user-type-select",
            grid: 4,
            validation: {
                type: {
                    value: "string",
                    message: "{field} must be a string"
                },
                required: {
                    value: false,
                    message: "{field} is required"
                }
            }
        },
        role: {
            group: 'details',
            label: "Role",
            type: "superadmin-user-role-select",
            grid: 4,
            validation: {
                type: {
                    value: "string",
                    message: "{field} must be a string"
                },
                required: {
                    value: false,
                    message: "{field} is required"
                }
            }
        },
        color: {
            group: 'default',
            label: "User Color",
            type: "color-picker",
            grid: 4,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        first_name: {
            group: 'details',
            label: "First Name",
            type: "text",
            grid: 6,
            validation: {
                type: {
                    value: "string",
                    message: "{field} must be a string"
                },
                required: {
                    value: false,
                    message: "{field} is required"
                }
            }
        },
        last_name: {
            group: 'details',
            label: "Last Name",
            type: "text",
            grid: 6,
            validation: {
                type: {
                    value: "string",
                    message: "{field} must be a string"
                },
                required: {
                    value: false,
                    message: "{field} is required"
                }
            }
        },
        address_line1: {
            group: 'default',
            label: "Address Line 1",
            type: "text",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        address_line2: {
            group: 'default',
            label: "Address Line 2",
            type: "text",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        city: {
            group: 'default',
            label: "City",
            type: "text",
            grid: 4,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        state: {
            group: 'default',
            label: "State",
            type: "text",
            grid: 4,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        postal_code: {
            group: 'default',
            label: "Postal Code",
            type: "text",
            grid: 4,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        phone: {
            group: 'default',
            label: "Phone",
            type: "text",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        timezone: {
            group: 'default',
            label: "Timezone",
            type: "timezone-select",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        bio: {
            group: 'default',
            label: "Bio",
            type: "textarea",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        }
    }
}

export type UserUpdateFragment = Omit<UserFragment, 'id' | 'created_at' | 'updated_at' | 'created_by_user' | 'updated_by_user' | 'user_number' | 'user_role' | 'user_type' | 'user_status'>

export const UserUpdateSchema: FormSchema<UserUpdateFragment> = {
    id: "user",
    title: "User",
    groups: {
        details: {
            label: 'Details' 
        }
    },
    fields: {
        status: {
            group: 'default',
            label: "Status",
            type: "user-status-select",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        email: {
            group: 'details',
            label: "Email",
            type: "email",
            grid: 12,
            editable: false,
            validation: {
                type: {
                    value: "string",
                    message: "{field} must be a string"
                },
                required: {
                    value: true,
                    message: "{field} is required"
                },
                email: {
                    message: "{field} is invalid"
                },
                email_available: {
                    message:  "{field} is invalid"
                }
            }
        },
        type: {
            group: 'details',
            label: "Department",
            type: "superadmin-user-type-select",
            grid: 4,
            validation: {
                type: {
                    value: "string",
                    message: "{field} must be a string"
                },
                required: {
                    value: false,
                    message: "{field} is required"
                }
            }
        },
        role: {
            group: 'details',
            label: "Role",
            type: "superadmin-user-role-select",
            grid: 4,
            validation: {
                type: {
                    value: "string",
                    message: "{field} must be a string"
                },
                required: {
                    value: false,
                    message: "{field} is required"
                }
            }
        },
        color: {
            group: 'default',
            label: "User Color",
            type: "color-picker",
            grid: 4,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        first_name: {
            group: 'details',
            label: "First Name",
            type: "text",
            grid: 6,
            validation: {
                type: {
                    value: "string",
                    message: "{field} must be a string"
                },
                required: {
                    value: false,
                    message: "{field} is required"
                }
            }
        },
        last_name: {
            group: 'details',
            label: "Last Name",
            type: "text",
            grid: 6,
            validation: {
                type: {
                    value: "string",
                    message: "{field} must be a string"
                },
                required: {
                    value: false,
                    message: "{field} is required"
                }
            }
        },
        address_line1: {
            group: 'default',
            label: "Address Line 1",
            type: "text",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        address_line2: {
            group: 'default',
            label: "Address Line 2",
            type: "text",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        city: {
            group: 'default',
            label: "City",
            type: "text",
            grid: 4,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        state: {
            group: 'default',
            label: "State",
            type: "text",
            grid: 4,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        postal_code: {
            group: 'default',
            label: "Postal Code",
            type: "text",
            grid: 4,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        phone: {
            group: 'default',
            label: "Phone",
            type: "text",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        timezone: {
            group: 'default',
            label: "Timezone",
            type: "timezone-select",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        },
        bio: {
            group: 'default',
            label: "Bio",
            type: "textarea",
            grid: 12,
            validation: {
                required: {
                    value: false,
                    message: '{field} is required'
                }
            }
        }
    }
}